import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const shareToken = params.get("token") || "";
  const [status, setStatus] = useState({ payment_status: "processing" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let currentAttempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        setAttempts(currentAttempts);
        if (data.payment_status === "paid") {
          // Redirect to the customer confirmation page — no more homepage dump.
          const share = shareToken ? `&token=${encodeURIComponent(shareToken)}` : "";
          navigate(`/booking/confirmed/${bookingId}?paid=1${share}`, { replace: true });
          return;
        }
        if (data.status === "expired") return;
        if (currentAttempts >= MAX_ATTEMPTS) return;
        currentAttempts += 1;
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        setStatus({ payment_status: "error" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, bookingId, shareToken, navigate]);

  const paid = status.payment_status === "paid";
  const expired = status.status === "expired";
  const timedOut = attempts >= MAX_ATTEMPTS && !paid;

  return (
    <section
      data-testid={TID.payment.return}
      className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-5"
    >
      <div className="glass rounded-3xl p-10 max-w-lg w-full text-center noise-overlay relative overflow-hidden">
        {paid ? (
          <>
            <CheckCircle2 size={56} className="text-gold mx-auto mb-4" />
            <h1 className="font-serif text-3xl mb-3">Booking confirmed</h1>
            <p className="text-white/70 mb-6">Your booking is paid and confirmed. A receipt is on its way.</p>
            <div data-testid={TID.payment.status} className="label-caps text-gold mb-6">Payment · Paid</div>
            <div className="flex gap-3 justify-center">
              <Link to={`/bookings/${bookingId}`} className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                View booking <ArrowRight size={14} />
              </Link>
              <button onClick={() => navigate("/bookings")} className="px-5 py-2.5 rounded-full text-sm border border-white/10 hover:border-white/30">
                All bookings
              </button>
            </div>
          </>
        ) : expired || timedOut ? (
          <>
            <XCircle size={56} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-serif text-3xl mb-3">Payment not completed</h1>
            <p className="text-white/70 mb-6">Your session expired or the payment didn’t go through. You can retry from your booking.</p>
            <Link to={`/bookings/${bookingId}`} className="btn-gold px-5 py-2.5 rounded-full text-sm inline-block">
              Back to booking
            </Link>
          </>
        ) : (
          <>
            <Clock size={56} className="text-electric mx-auto mb-4 animate-pulse" />
            <h1 className="font-serif text-3xl mb-3">Finalising payment…</h1>
            <p className="text-white/70 mb-2">Hang tight — Stripe is confirming your payment.</p>
            <div data-testid={TID.payment.status} className="label-caps text-electric">
              Payment · {status.payment_status || "processing"}
            </div>
            <div className="mt-6 w-10 h-10 rounded-full border-2 border-electric border-t-transparent animate-spin mx-auto" />
          </>
        )}
      </div>
    </section>
  );
}
